// Página de Política de Privacidad de la Plataforma
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Shield, Calendar, Lock } from 'lucide-react'
import client from '../api/client'
import Card from '../components/ui/Card'

export default function PrivacyPolicy() {
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'

  return (
    <div className="py-8 px-4 sm:py-12 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8 animate-fade-in font-body">
      {/* Encabezado */}
      <div className="flex items-center gap-4 border-b border-border-subtle pb-6">
        <div className="p-3 rounded-2xl bg-cyan/10 border border-cyan/20 text-cyan">
          <Shield className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary">
            Política de Privacidad
          </h1>
          <p className="text-xs text-text-muted mt-1 flex items-center gap-1.5 font-mono">
            <Calendar className="w-3.5 h-3.5" /> Última actualización: 13 de junio de 2026
          </p>
        </div>
      </div>

      <div className="space-y-6 text-sm text-text-secondary leading-relaxed">
        <p>
          En <strong className="font-semibold text-text-primary">{nombreSistema}</strong>, valoramos y respetamos su privacidad. Esta Política de Privacidad describe cómo recopilamos, utilizamos, almacenamos, divulgamos y protegemos su información personal cuando visita o utiliza nuestra aplicación web de predicciones y apuestas deportivas.
        </p>

        {/* Sección 1 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            1. Información que Recopilamos
          </h3>
          <p>
            Recopilamos información personal básica necesaria para proveer el servicio de predicciones y las transacciones de créditos de forma segura:
          </p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>
              <strong className="font-semibold text-text-primary">Información de Registro:</strong> Su nombre, apellido, dirección de correo electrónico, país y contraseña cifrada.
            </li>
            <li>
              <strong className="font-semibold text-text-primary">Datos Financieros y Transacciones:</strong>
              <ul className="list-circle list-inside pl-6 space-y-0.5 mt-0.5">
                <li>Comprobantes de depósito (vouchers): Las imágenes cargadas por usted al solicitar recargas de saldo para verificar los pagos bancarios.</li>
                <li>Códigos QR Bancarios: Las imágenes de QR cargadas por usted para transferirle los fondos que solicite retirar de su saldo.</li>
              </ul>
            </li>
          </ul>
        </section>

        {/* Sección 2 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            2. Uso de la Información
          </h3>
          <p>
            Utilizamos su información personal únicamente para los siguientes propósitos:
          </p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>Administrar su cuenta, validar sus predicciones deportivas y actualizar su posición en el ranking público.</li>
            <li>Procesar, verificar y autorizar de forma manual sus solicitudes de carga y de retiro de fondos.</li>
            <li>Enviar alertas operativas (como restablecimiento de contraseña o confirmación de transacciones) directamente por correo electrónico.</li>
            <li>Prevenir fraudes, multicuenta o abusos de seguridad dentro de las ligas y pozos de apuestas.</li>
          </ul>
        </section>

        {/* Sección 3 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            3. Almacenamiento y Protección de Datos
          </h3>
          <p>
            Toda la información personal y de las transacciones se almacena en bases de datos protegidas y seguras. Las contraseñas de los usuarios se cifran utilizando algoritmos seguros y <strong className="font-semibold text-text-primary">nunca</strong> son visibles en texto plano. Las imágenes de comprobantes y códigos QR se almacenan físicamente en el directorio seguro del servidor y están resguardadas de la lectura externa directa.
          </p>
        </section>

        {/* Sección 4 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            4. Retención de los Datos
          </h3>
          <p>
            Conservamos su información personal durante el tiempo que sea necesario para cumplir con los fines para los cuales fue recopilada y para cumplir con nuestras obligaciones legales de auditoría contable. Cuando el usuario decide eliminar su perfil o si este es baneado permanentemente por infringir las normas, sus datos personales se borran o anonimizan de forma segura de las bases de datos de <strong className="font-semibold text-text-primary">{nombreSistema}</strong>.
          </p>
        </section>

        {/* Sección 5 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            5. Compartir Información con Terceros
          </h3>
          <p>
            <strong className="font-semibold text-text-primary">{nombreSistema}</strong> no vende, alquila ni comercializa su información personal bajo ninguna circunstancia. Tampoco compartimos sus datos con anunciantes ni terceros externos, salvo requerimiento expreso de una orden judicial emitida por autoridad competente de conformidad con las leyes vigentes del territorio nacional.
          </p>
        </section>

        {/* Sección 6 */}
        <section className="space-y-2">
          <h3 className="font-display font-bold text-base text-text-primary uppercase tracking-wider">
            6. Derechos del Usuario (Acceso, Rectificación y Supresión)
          </h3>
          <p>
            Usted tiene derecho a acceder, rectificar o actualizar sus datos personales (como su correo, contraseña o nombre) en cualquier momento desde la sección de <strong className="font-semibold text-text-primary">Mi Perfil</strong> de la aplicación. Para solicitar la eliminación definitiva de su cuenta de usuario y de todo su historial contable asociado, puede ponerse en contacto directo con el administrador de la plataforma.
          </p>
        </section>
      </div>

      {/* Sello de seguridad */}
      <Card className="p-4 bg-navy-dark/40 border border-border-subtle rounded-xl flex items-center gap-3">
        <Lock className="w-6 h-6 text-cyan shrink-0 animate-pulse" />
        <p className="text-xs text-text-muted leading-relaxed">
          Su privacidad es nuestra prioridad absoluta. Implementamos medidas de seguridad técnicas e institucionales para salvaguardar sus datos en <strong className="font-semibold text-text-primary">{nombreSistema}</strong> contra alteraciones, accesos no autorizados o divulgación.
        </p>
      </Card>
    </div>
  )
}
